import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Grid from "@material-ui/core/Grid";
import Chip from "@material-ui/core/Chip";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Typography from "@material-ui/core/Typography";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { CATEGORY_LABELS } from "../../pages/KnowledgeBase/constants";

const useStyles = makeStyles((theme) => ({
  hint: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    display: "block",
  },
  chips: { display: "flex", flexWrap: "wrap", gap: 4 },
  counter: {
    display: "flex",
    justifyContent: "space-between",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
}));

const EMPTY = {
  title: "",
  category: "faq",
  status: "active",
  content: "",
  queueIds: [],
};

const KnowledgeArticleModal = ({ open, onClose, articleId, onSaved }) => {
  const classes = useStyles();
  const [form, setForm] = useState(EMPTY);
  const [queues, setQueues] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api
      .get("/queue")
      .then((r) => setQueues(r.data || []))
      .catch(() => setQueues([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!articleId) {
      setForm(EMPTY);
      return;
    }
    api
      .get(`/knowledge-base/${articleId}`)
      .then(({ data }) =>
        setForm({
          title: data.title || "",
          category: data.category || "faq",
          status: data.status || "active",
          content: data.content || "",
          queueIds: (data.queues || []).map((q) => q.id),
        })
      )
      .catch(toastError);
  }, [open, articleId]);

  const set = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      const { data } = articleId
        ? await api.put(`/knowledge-base/${articleId}`, payload)
        : await api.post("/knowledge-base", payload);
      toast.success(
        articleId
          ? "Artigo atualizado. Reindexe para a IA usar a versão nova."
          : "Artigo criado. Reindexe para a IA começar a usá-lo."
      );
      if (onSaved) onSaved(data);
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  }, [form, articleId, onSaved, onClose]);

  const chars = form.content.length;
  // Mesma heurística do backend (chars/4) só para dar noção de custo.
  const approxChunks = Math.max(1, Math.ceil(chars / 4 / 400));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {articleId ? "Editar artigo" : "Novo artigo"}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} style={{ marginTop: 4 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Título *"
              value={form.title}
              onChange={set("title")}
              helperText="Aparece no prompt da IA — seja descritivo."
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              select
              fullWidth
              label="Categoria"
              value={form.category}
              onChange={set("category")}
            >
              {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              select
              fullWidth
              label="Status"
              value={form.status}
              onChange={set("status")}
              helperText={
                form.status === "draft" ? "Rascunho não é usado pela IA." : " "
              }
            >
              <MenuItem value="active">Ativo</MenuItem>
              <MenuItem value="draft">Rascunho</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Filas (opcional)</InputLabel>
              <Select
                multiple
                label="Filas (opcional)"
                value={form.queueIds}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, queueIds: e.target.value }))
                }
                renderValue={(selected) => (
                  <div className={classes.chips}>
                    {selected.map((id) => {
                      const queue = queues.find((q) => q.id === id);
                      return (
                        <Chip
                          key={id}
                          size="small"
                          label={queue ? queue.name : `#${id}`}
                        />
                      );
                    })}
                  </div>
                )}
              >
                {queues.map((queue) => (
                  <MenuItem key={queue.id} value={queue.id}>
                    {queue.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" className={classes.hint}>
              Sem fila selecionada, o artigo vale para todas. Com filas, só é
              consultado nos atendimentos daquelas filas.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={12}
              label="Conteúdo *"
              value={form.content}
              onChange={set("content")}
              placeholder={
                "Escreva em blocos separados por linha em branco.\n\nCada bloco vira um trecho buscável, então mantenha pergunta e resposta juntas no mesmo parágrafo."
              }
            />
            <div className={classes.counter}>
              <Typography variant="caption">
                {chars.toLocaleString("pt-BR")} caracteres
              </Typography>
              <Typography variant="caption">
                ~{approxChunks} {approxChunks === 1 ? "trecho" : "trechos"} ao
                indexar
              </Typography>
            </div>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSave}
          disabled={saving || !form.title.trim() || form.content.trim().length < 10}
        >
          {saving ? "Salvando…" : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

KnowledgeArticleModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  articleId: PropTypes.number,
  onSaved: PropTypes.func,
};

KnowledgeArticleModal.defaultProps = {
  articleId: null,
  onSaved: undefined,
};

export default KnowledgeArticleModal;
